import { API_CONFIG } from '../constants';
import { getStoredServiceToken, setStoredServiceToken, getRemainingTokenTime } from '../utils/helpers';

export class SignInError extends Error {
    status: number;
    errorCode?: string;
    payload?: unknown;

    constructor(message: string, status: number, errorCode?: string, payload?: unknown) {
        super(message);
        this.name = 'SignInError';
        this.status = status;
        this.errorCode = errorCode;
        this.payload = payload;
    }
}

/**
 * Fetch service account token using client_credentials grant.
 * Returns { token, expiresIn } where expiresIn is seconds until expiry.
 */
export async function fetchServiceAccountToken(): Promise<{ token: string | null; expiresIn: number }> {
    // Check for a valid cached token first
    const existing = getStoredServiceToken();
    if (existing) {
        return { token: existing, expiresIn: getRemainingTokenTime() };
    }

    const tokenUrl =
        process.env.REACT_APP_TOKEN_URL ||
        `${process.env.REACT_APP_AUTH_ISSUER}/protocol/openid-connect/token`;
    const clientId = process.env.REACT_APP_SERVICE_CLIENT_ID;
    const clientSecret = process.env.REACT_APP_CLIENT_SECRET;

    if (!clientSecret) {
        console.warn("REACT_APP_CLIENT_SECRET not set; skipping service account token");
        return { token: null, expiresIn: 0 };
    }

    const params = new URLSearchParams({
        client_id: clientId,
        client_secret: clientSecret,
        grant_type: "client_credentials",
    });

    const response = await fetch(tokenUrl, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
    });

    if (!response.ok) {
        const errBody = await response.text();
        throw new Error(`Service account token failed: ${response.status} ${errBody}`);
    }

    const data = await response.json();
    const token = data.access_token ?? null;
    const expiresIn = typeof data.expires_in === "number" ? data.expires_in : 0;
    if (token && expiresIn) {
        setStoredServiceToken(token, expiresIn);
    }
    return { token, expiresIn };
}

export async function signIn(podIp: string, accessToken: string): Promise<unknown> {
    const params = new URLSearchParams({
        component_id: '5',
        pod_ip: podIp,
    });
    const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SIGN_IN}?${params.toString()}`, {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${accessToken}`,
        }
    });

    if (!response.ok) {
        const raw = await response.text().catch(() => '');
        let payload: unknown = raw;
        let errorCode: string | undefined;

        if (raw) {
            try {
                const parsed = JSON.parse(raw) as { errorCode?: string };
                payload = parsed;
                errorCode = parsed.errorCode;
            } catch {
                // Keep raw payload for non-JSON responses.
            }
        }

        throw new SignInError(
            `Sign in failed (${response.status})${errorCode ? ` [${errorCode}]` : ''}`,
            response.status,
            errorCode,
            payload
        );
    }

    return response.json();
}